/** The public shape written by scripts/fetch-groups.js. No PII by construction. */
export interface Group {
  id: string;
  name: string;
  rawName: string;
  /** CCB department, e.g. "2026 Fall". Not consistently named — see README. */
  term: string;
  /** Same, minus the year: "Fall". Shown only when the page lists two terms. */
  termLabel: string;
  /** Sanitized HTML — a tiny attribute-free tag set. Null when there is no real text. */
  description: string | null;
  descriptionText: string | null;
  excerpt: string;
  campus: string;
  campusKey: string;
  /** Where the group actually meets: the campus, or e.g. "Home in Lexington". */
  location: string;
  /** What the Location filter matches: a campus slug, or "homes". */
  locationKey: string;
  meetsAtHome: boolean;
  audience: string;
  audienceKey: string;
  /** Raw CCB value, e.g. "1st and 3rd Saturday". Displayed as-is. */
  meetingDay: string;
  /** Weekday derived from meetingDay, or "other". Filtered on. */
  dayKey: string;
  /**
   * What to show for "when": a clock time like "6:00-7:15pm" when one could be
   * found, otherwise CCB's vague bucket ("Evening"). CCB's own field is usually
   * just the bucket, so the specific time is read out of the description.
   */
  meetingTime: string;
  /** False when meetingTime is still only a bucket. */
  meetingTimeIsSpecific: boolean;
  /** CCB's original value. Drives sorting, and shown in the detail panel. */
  meetingTimeBucket: string;
  /** From CCB's `childcare_provided`. Shown as "Kids Ministry". */
  kidsMinistry: boolean;
  /** Leader's name. Never an email, phone number, or CCB id. */
  leaderFirstName: string | null;
  leaderLastName: string | null;
  /** e.g. "KC" — the monogram shown in the avatar when there is no photo. */
  leaderInitials: string | null;
  /**
   * Put the Crossroads mark in the circle instead of a face. Set by staff for a
   * listing credited to a role rather than a person, where a portrait of one
   * leader contradicts the byline. Older data files predate the field.
   */
  logoAvatar?: boolean;
  capacity: number | null;
  /** People in the group now — the same number CCB's public list shows. */
  currentMembers: number | null;
  spotsLeft: number | null;
  isFull: boolean;
  /**
   * Local path to the group's photo, e.g. "/data/group-photos/4204.jpg?v=a1b2".
   * Null when CCB has no photo for the group — which is every group until staff
   * upload one. Never a CCB URL: those are presigned and expire in an hour.
   */
  photo: string | null;
  /**
   * Square crop of the leader's face, cut from `photo` during the refresh.
   * This is what the circular avatar shows — the full photo is a landscape
   * banner and would render as a slice of the group name. Null when no face
   * was detected, and the avatar falls back to the initials monogram.
   */
  photoFace: string | null;
  /** Pinned to the bottom of the list by config.json `sortLast`. */
  sortLast?: boolean;
  /**
   * Position in config.json `sortFirst`, or null for an ordinary group.
   * Featured listings (the Midweek offerings) lead the list in this order.
   */
  featuredRank?: number | null;
  signupUrl: string;
}

export interface FacetOption {
  key: string;
  label: string;
}

/** One step of the how-to strip. Copy lives in config.json, not in the code. */
export interface HowToStep {
  label: string;
  text: string;
}

/**
 * How a group's photo is presented.
 *
 * `banner` suits the landscape artwork CCB holds today; `avatar` suits square
 * headshots and keeps photos from leading the card. Set in config.json.
 */
export type PhotoStyle = 'banner' | 'avatar';

export interface GroupData {
  meta: {
    generatedAt: string;
    terms: string[];
    groupCount: number;
    sample?: boolean;
    site: {
      title?: string;
      tagline?: string;
      /** The three-step orientation strip under the tagline. */
      howTo?: HowToStep[];
      /** Defaults to "banner" — see PhotoStyle. */
      photoStyle?: PhotoStyle;
      signupNote?: string;
    };
  };
  facets: {
    /** Campuses, plus a "Homes" option when any group meets in a house. */
    locations: FacetOption[];
    days: FacetOption[];
    audiences: FacetOption[];
  };
  groups: Group[];
}

export interface Filters {
  /** Free text. Unlike the pills this one ranks as well as narrows. */
  query: string;
  location: string | null;
  /** Multi-select: "I'm free Tuesday or Thursday" is the common case. */
  days: string[];
  audience: string | null;
  kidsMinistry: boolean;
}

export const EMPTY_FILTERS: Filters = {
  query: '',
  location: null,
  days: [],
  audience: null,
  kidsMinistry: false,
};
